/**
 * Super-admin storefront access from the dashboard.
 *
 * Query `?admin=edit` — draft + full owner chrome.
 * Query `?admin=preview` — draft with every editor hidden.
 * Query `?admin=live` — published storefront (same as a shopper).
 *
 * Nested storefront navigations drop the query, so the mode is kept in
 * shared state and sessionStorage for this store id.
 */
export type StoreAdminAccessMode = 'edit' | 'preview' | null

const STATE_KEY = 'store-admin-access'
const STORAGE_PREFIX = 'cs-admin-store-access:'

function storageKey(storeId: string): string {
  return `${STORAGE_PREFIX}${storeId}`
}

function readStored(storeId: string): StoreAdminAccessMode {
  if (!import.meta.client || !storeId) return null
  try {
    const value = sessionStorage.getItem(storageKey(storeId))
    return value === 'edit' || value === 'preview' ? value : null
  }
  catch {
    return null
  }
}

function writeStored(storeId: string, mode: StoreAdminAccessMode): void {
  if (!import.meta.client || !storeId) return
  try {
    if (!mode) sessionStorage.removeItem(storageKey(storeId))
    else sessionStorage.setItem(storageKey(storeId), mode)
  }
  catch {
    // Private mode / quota — the in-memory state still covers this tab.
  }
}

function queryAdmin(query: object): string | null {
  const raw = (query as { admin?: unknown }).admin
  const value = Array.isArray(raw) ? raw[0] : raw
  return typeof value === 'string' ? value : null
}

function storeIdFromRoute(params: object): string {
  const raw = (params as { id?: unknown }).id
  return String(Array.isArray(raw) ? raw[0] : raw || '')
}

function isStorefrontPath(path: string | undefined): boolean {
  if (!path) return false
  return path === '/store' || path.startsWith('/store/')
}

export function applyStoreAdminAccessFromRoute(
  route: { path?: string, query: object, params: object },
  mode: Ref<StoreAdminAccessMode>,
): void {
  if (!isStorefrontPath(route.path)) return

  const id = storeIdFromRoute(route.params)
  const query = queryAdmin(route.query)

  if (query === 'edit' || query === 'preview') {
    mode.value = query
    writeStored(id, query)
  }
  else if (query === 'live') {
    mode.value = null
    writeStored(id, null)
  }
  else if (!mode.value) {
    const stored = readStored(id)
    if (stored) mode.value = stored
  }
  else if (id) {
    writeStored(id, mode.value)
  }

  try {
    const livePreview = useStoreLivePreview()
    if (mode.value === 'preview') livePreview.value = true
    else if (query === 'edit' || query === 'live') livePreview.value = false
  }
  catch {
    // Called outside a Nuxt instance (tests / plugins).
  }
}

type StoreAdminRoute = { path?: string, query: object, params: object }

export function useStoreAdminAccess(routeSource?: StoreAdminRoute) {
  const mode = useState<StoreAdminAccessMode>(STATE_KEY, () => null)
  if (routeSource) {
    applyStoreAdminAccessFromRoute(routeSource, mode)
  }
  else {
    const route = useRoute()
    applyStoreAdminAccessFromRoute(route, mode)
    if (import.meta.client) {
      watch(
        () => [route.path, route.query.admin, route.params.id] as const,
        () => {
          applyStoreAdminAccessFromRoute(route, mode)
        },
      )
    }
  }

  return {
    mode,
    isStudio: computed(() => mode.value === 'edit'),
    isPreview: computed(() => mode.value === 'preview'),
    headerValue: computed(() =>
      mode.value === 'edit' || mode.value === 'preview' ? mode.value : null,
    ),
  }
}

export function storeAdminAccessHeader(
  mode: StoreAdminAccessMode | null | undefined,
): string | null {
  return mode === 'edit' || mode === 'preview' ? mode : null
}
